import { useMemo, useState } from 'react';
import { getProjectModuleCapabilities } from '../config/projectCapabilities';
import { useAuth } from '../context/AuthContext';
import {
  defaultTimeLogEmployeeId,
  timeLogEmployeeOptions,
  type TimeLogEmployeeOption,
} from '../utils/taskTimeLog';

export type TimeLogEmployeeChoice = {
  selfEmployeeId: number | null;
  /** HR Admins / Team Leads pick the employee; members always log as themselves. */
  canLogForOthers: boolean;
  options: TimeLogEmployeeOption[];
  selectedEmployeeId: number | null;
  select: (employeeId: number) => void;
  /** Why no entry can be saved yet, or null. */
  missingMessage: string | null;
  /** The `employeeId` to send with a new entry (only when choosing for others). */
  requestEmployeeId: number | undefined;
};

/**
 * Whose time a new entry records. The API always records the session
 * employee; HR Admins / Team Leads (who may have no employee profile) pick one
 * of the task's assignees instead.
 */
export function useTimeLogEmployee(input: {
  assignees: readonly TimeLogEmployeeOption[];
  members: readonly TimeLogEmployeeOption[];
}): TimeLogEmployeeChoice {
  const auth = useAuth();
  const selfEmployeeId = auth.user?.employeeId ?? null;
  const canLogForOthers =
    getProjectModuleCapabilities(auth.permissions, auth.roles).scope !== 'MEMBER';
  const { assignees, members } = input;
  const options = useMemo(
    () =>
      canLogForOthers
        ? timeLogEmployeeOptions({ assignees, members, selfEmployeeId })
        : [],
    [assignees, canLogForOthers, members, selfEmployeeId],
  );
  const [logFor, setLogFor] = useState<number | null>(null);
  const selectedEmployeeId =
    logFor != null && options.some(item => item.employeeId === logFor)
      ? logFor
      : defaultTimeLogEmployeeId(options, selfEmployeeId);
  const missingMessage =
    canLogForOthers && selectedEmployeeId == null
      ? options.length
        ? 'Select the employee this time entry is for.'
        : 'Assign this task to a project member before logging time.'
      : null;
  return {
    selfEmployeeId,
    canLogForOthers,
    options,
    selectedEmployeeId,
    select: setLogFor,
    missingMessage,
    requestEmployeeId:
      canLogForOthers && selectedEmployeeId != null ? selectedEmployeeId : undefined,
  };
}
